if (!customElements.get('shipping-estimator')) {
	customElements.define('shipping-estimator', class ShippingEstimator extends HTMLElement {
		connectedCallback() {
			if (this.listenersConnected) return;
			this.listenersConnected = true;
			this.form = this.querySelector('form');
			this.provinceInput = this.querySelector('[name="shipping_province"]');
			this.zipInput = this.querySelector('[name="shipping_zip"]');
			this.submitButton = this.querySelector('[type="submit"]');
			this.status = this.querySelector('.product-shipping-estimator-status');
			this.results = this.querySelector('.product-shipping-estimator-results');
			this.rootUrl = this.dataset.rootUrl || '/';

			this.submitListener = this.onSubmit.bind(this);
			this.cartListener = (event) => {
				if (event.type === 'cart:item-add') this.dataset.cartEmpty = 'false';
				this.invalidateRates('Your cart changed. Check shipping rates again.');
			};
			this.destinationListener = () => this.invalidateRates('Your destination changed. Check shipping rates again.');
			this.cartEvents = ['cart:item-add', 'cart:item-remove', 'cart:item-quantity-update'];
			this.form.addEventListener('submit', this.submitListener);
			this.form.addEventListener('input', this.destinationListener);
			this.cartEvents.forEach((name) => document.addEventListener(name, this.cartListener));
		}

		disconnectedCallback() {
			if (!this.listenersConnected) return;
			this.form.removeEventListener('submit', this.submitListener);
			this.form.removeEventListener('input', this.destinationListener);
			this.cartEvents.forEach((name) => document.removeEventListener(name, this.cartListener));
			this.requestId = (this.requestId || 0) + 1;
			this.setLoading(false);
			this.listenersConnected = false;
		}

		invalidateRates(message) {
			this.requestId = (this.requestId || 0) + 1;
			this.clearResults();
			this.setLoading(false);
			this.setStatus(message);
		}

		async onSubmit(event) {
			event.preventDefault();
			if (this.submitButton.disabled) return;
			const requestId = this.requestId = (this.requestId || 0) + 1;
			const zip = this.zipInput.value.trim();
			const province = this.provinceInput.value;

			if (!province) {
				this.showError('Select your state.');
				this.provinceInput.focus();
				return;
			}

			if (!/^\d{5}(?:-\d{4})?$/.test(zip)) {
				this.showError('Enter a valid 5-digit US ZIP code.');
				this.zipInput.focus();
				return;
			}

			this.setLoading(true);
			this.clearResults();
			this.setStatus('Checking your cart…');

			try {
				const cartResponse = await this.fetchWithTimeout(`${this.rootUrl}cart.js`, {
					headers: { Accept: 'application/json' },
				});
				if (!cartResponse.ok) throw new Error('Cart unavailable');

				const cart = await cartResponse.json();
				if (requestId !== this.requestId) return;
				if (!cart.item_count) {
					this.dataset.cartEmpty = 'true';
					this.showError('Your cart is empty. Select your purchase option and click Add to cart above, then check rates again. Individual products ship free.');
					return;
				}

				this.dataset.cartEmpty = 'false';
				const params = new URLSearchParams({
					'shipping_address[zip]': zip,
					'shipping_address[country]': 'United States',
					'shipping_address[province]': province,
				});
				const prepareUrl = `${this.rootUrl}cart/prepare_shipping_rates.json?${params}`;
				const ratesUrl = `${this.rootUrl}cart/async_shipping_rates.json?${params}`;
				const prepareResponse = await this.fetchWithTimeout(prepareUrl, {
					method: 'POST',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					body: '',
				});

				if (requestId !== this.requestId) return;
				if (!prepareResponse.ok) {
					throw await this.responseError(prepareResponse);
				}

				this.setStatus('Calculating rates…');
				const rates = await this.pollForRates(ratesUrl, requestId);
				if (requestId === this.requestId) this.renderRates(rates, zip);
			} catch (error) {
				if (requestId !== this.requestId) return;
				this.showError(error.message || 'Shipping rates could not be calculated. Please try again.');
				document.dispatchEvent(new CustomEvent('tt:shipping-estimate', { detail: { status: 'error' } }));
			} finally {
				if (requestId === this.requestId) this.setLoading(false);
			}
		}

		async pollForRates(url, requestId) {
			for (let attempt = 0; attempt < 12; attempt += 1) {
				if (requestId !== this.requestId) return [];
				const response = await this.fetchWithTimeout(url, { headers: { Accept: 'application/json' } });
				if (!response.ok) throw await this.responseError(response);

				const data = await response.json();
				if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
				await new Promise((resolve) => setTimeout(resolve, 500));
			}

			throw new Error('Shipping rates are taking longer than expected. Please try again.');
		}

		async fetchWithTimeout(url, options) {
			const controller = new AbortController();
			const timeout = setTimeout(() => controller.abort(), 15000);
			try {
				return await fetch(url, { ...options, signal: controller.signal });
			} catch (error) {
				if (error.name === 'AbortError') {
					throw new Error('Shipping check timed out. Please try again, or view shipping options at checkout.');
				}
				throw error;
			} finally {
				clearTimeout(timeout);
			}
		}

		async responseError(response) {
			try {
				const data = await response.json();
				const message = data.message || data.error || Object.values(data)[0];
				if (Array.isArray(message)) return new Error(message[0]);
				if (typeof message === 'string') return new Error(message);
			} catch (error) {
				// Fall through to the customer-friendly error below.
			}
			return new Error('Shipping rates could not be calculated for this ZIP code.');
		}

		renderRates(rates, zip) {
			this.clearResults();

			if (!rates.length) {
				this.showError(`No shipping rates are available for ${zip}. Contact us for help.`);
				return;
			}

			const sortedRates = rates.filter((rate) => {
				return rate.price !== null && rate.price !== undefined &&
					String(rate.price).trim() !== '' && Number.isFinite(Number(rate.price)) && Number(rate.price) >= 0;
			}).sort((first, second) => Number(first.price) - Number(second.price));
			if (!sortedRates.length) {
				this.showError('Shipping prices are unavailable. Please check shipping options at checkout.');
				return;
			}
			sortedRates.slice(0, 1).forEach((rate) => {
				const item = document.createElement('li');
				const name = document.createElement('span');
				const price = document.createElement('strong');

				name.textContent = this.rateLabel(rate);
				price.textContent = this.formatPrice(rate.price, rate.currency);
				item.append(name, price);
				this.results.append(item);
			});

			this.results.hidden = false;
			this.setStatus(`Lowest-priced shipping estimate for ${zip}. All available options are shown at checkout:`);
			document.dispatchEvent(new CustomEvent('tt:shipping-estimate', {
				detail: {
					status: 'success',
					rateCount: rates.length,
					freeRateAvailable: Number(sortedRates[0].price) === 0,
				},
			}));
		}

		rateLabel(rate) {
			const label = rate.presentment_name || rate.name || 'Shipping';
			if (rate.delivery_range && rate.delivery_range.length === 2) {
				return `${label} · ${rate.delivery_range[0]}–${rate.delivery_range[1]}`;
			}
			if (rate.delivery_date) return `${label} · arrives ${rate.delivery_date}`;
			if (rate.delivery_days && rate.delivery_days.length === 2) {
				return `${label} · ${rate.delivery_days[0]}–${rate.delivery_days[1]} business days`;
			}
			if (rate.delivery_days && rate.delivery_days.length === 1) {
				return `${label} · ${rate.delivery_days[0]} business days`;
			}
			return label;
		}

		formatPrice(value, currency) {
			const amount = Number(value);
			if (amount === 0) return 'Free';
			try {
				return new Intl.NumberFormat(undefined, {
					style: 'currency',
					currency: currency || window.Shopify?.currency?.active || 'USD',
				}).format(amount);
			} catch (error) {
				return `$${amount.toFixed(2)}`;
			}
		}

		setLoading(isLoading) {
			this.submitButton.disabled = isLoading;
			this.submitButton.setAttribute('aria-busy', String(isLoading));
			this.submitButton.textContent = isLoading ? 'Checking…' : 'Check cart shipping';
		}

		setStatus(message, isError = false) {
			this.status.textContent = message;
			this.status.classList.toggle('is-error', isError);
		}

		showError(message) {
			this.clearResults();
			this.setStatus(message, true);
		}

		clearResults() {
			this.results.replaceChildren();
			this.results.hidden = true;
		}
	});
}
